import React from 'react';

import { CarouselArrow } from '../../components/CarouselArrow/CarouselArrow';
import { CarouselDots } from '../../components/CarouselDots/CarouselDots';
import { SportIcon } from '../../components/SportIcon/SportIcon';
import { useCarousel } from '../../hooks/useCarousel';
import { useInView } from '../../hooks/useInView';
import type { Testimonials } from './slides';

import './TestimonialsSection.css';

type TestimonialsSectionProps = {
    data: Testimonials;
};

export function TestimonialsSection({ data }: TestimonialsSectionProps) {
    const { badge, items } = data;
    const total = items.length;
    const { index, setIndex, goPrev, goNext } = useCarousel({ total });
    const [ref, inView] = useInView<HTMLDivElement>();

    if (total === 0) { return null; }

    const current = items[index];

    return (
        <section className="fa-tm">
            <div className="fa-tm__viewport">
                {items.map((item, i) => (
                    <img
                        key={item.id}
                        className={`fa-tm__bg${i === index ? ' fa-tm__bg--active' : ''}`}
                        src={item.backgroundImage}
                        alt=""
                        aria-hidden="true"
                    />
                ))}
                <div className="fa-tm__overlay" aria-hidden="true" />
                <div className="fa-tm__top">
                    <span className="fa-tm__badge faves-eyebrow--small faves-glass">{badge}</span>
                    <CarouselDots
                        count={total}
                        activeIndex={index}
                        onSelect={setIndex}
                        label={(i) => `Testimonial ${i + 1}`}
                        color="var(--faves-accent)"
                        inactiveOpacity={0.4}
                        hoverOpacity={0.7}
                        withBackdropBlur
                    />
                </div>
                <div ref={ref} className={`fa-tm__content${inView ? ' fa-tm__content--in-view' : ''}`}>
                    <blockquote className="fa-tm__quote faves-display">{current.quote}</blockquote>
                    <div className="fa-tm__row">
                        <div className="fa-tm__eyebrow">
                            <img className="fa-tm__avatar" src={current.avatarImage} alt="" aria-hidden="true" />
                            <span className="fa-tm__name">{current.name}</span>
                            <span className="fa-tm__sport-icon">
                                {current.sportIconUrl
                                    ? <img src={current.sportIconUrl} alt="" loading="lazy" decoding="async" />
                                    : <SportIcon iconKey={current.sportIcon} />}
                            </span>
                        </div>
                        <div className="fa-tm__arrows">
                            <CarouselArrow
                                direction="prev"
                                ariaLabel="Previous testimonial"
                                onClick={goPrev}
                                focusColor="var(--faves-accent)"
                            />
                            <CarouselArrow
                                direction="next"
                                ariaLabel="Next testimonial"
                                onClick={goNext}
                                focusColor="var(--faves-accent)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
